/**
 * The queue's order, shared by the list that shows it and the hook that
 * reads the riders in that order (so the rows on screen are named first).
 * Pure, so it is unit-tested on its own.
 */

import { Conversation } from './types';

type WaitingFields = Pick<
  Conversation,
  'status' | 'last_message' | 'handled_by' | 'waiting_since'
>;

/**
 * When the rider started waiting: ant's waitingSince, their first message
 * after Support's last public reply, so writing again does not reset the
 * clock. The newest public message must be theirs and the session a
 * person's to answer. Null when Support spoke last, the session is closed,
 * or the assistant has it. Without waitingSince (an older ant) it falls
 * back to the rider's newest message.
 */
export function waitingSince(c: WaitingFields): string | null {
  if (c.status === 'closed' || c.handled_by === 'assistant') return null;
  if (c.last_message?.sender_type !== 'user') return null;
  return c.waiting_since || c.last_message.created_on || null;
}

const ms = (iso: string | null | undefined) => (iso ? Date.parse(iso) || 0 : 0);

/**
 * Queue order: urgent first, then riders waiting for an answer, the one
 * waiting longest first (by waitingSince, the "Waiting Nm" chip's clock, so
 * a rider who writes twice does not sink), then the answered ones, the most
 * recently touched first (the chat an agent just answered lands right under
 * the waiting band). Closed sessions: most recently closed first.
 */
export function compareConversations(a: Conversation, b: Conversation): number {
  if (a.status === 'closed' || b.status === 'closed') {
    return ms(b.closed_at ?? b.updated_on) - ms(a.closed_at ?? a.updated_on);
  }
  const urgent = Number(b.priority === 'urgent') - Number(a.priority === 'urgent');
  if (urgent) return urgent;
  const waitingA = waitingSince(a);
  const waitingB = waitingSince(b);
  const waiting = Number(waitingB !== null) - Number(waitingA !== null);
  if (waiting) return waiting;
  if (waitingA !== null && waitingB !== null) {
    return ms(waitingA) - ms(waitingB) || ms(a.updated_on) - ms(b.updated_on);
  }
  return ms(b.updated_on) - ms(a.updated_on);
}
